import { analyze, keyName, pinName, pinNode, placedOrder, potentialOf, wireNode, type Analysis } from './analyze';
import { loc } from './geometry';
import { LIB } from './library';
import type { Placed, Project, Wire } from './types';

/*
 * Navigators: editable views over the project (EPLAN terminal strip, connection,
 * cable and device navigators). Rows point at the symbols and wires of the
 * project, so edits change the schematic directly.
 */

const tagSort = (a: string, b: string) => a.localeCompare(b, 'tr', { numeric: true });

/** Device pins electrically connected to pin i of a symbol (excluding the symbol itself). */
export function pinTargets(A: Analysis, it: Placed, i: number): string[] {
  const r = A.uf.find(pinNode(it.s, i)), out: string[] = [];
  for (const x of A.all) {
    const role = LIB[x.s.type].role;
    if (x.s.id === it.s.id || ['brk', 'text', 'pot', 'cable', 'sl', 'box'].includes(role)) continue;
    LIB[x.s.type].pins.forEach((_, j) => {
      if (A.uf.find(pinNode(x.s, j)) === r) out.push(pinName(A, x.s, j));
    });
  }
  return out;
}

/* ---------------- terminal strips ---------------- */

export interface TermRow {
  it: Placed;
  no: string;
  /** panel side (upper pin) */
  inner: string[];
  /** field side (lower pin) */
  outer: string[];
  pot: string;
  bridge: string;
  part: string;
  loc: string;
}
export interface Strip {
  key: string;
  name: string;
  rows: TermRow[];
}

export function terminalStrips(A: Analysis): Strip[] {
  const m = new Map<string, Placed[]>();
  for (const it of A.all)
    if (LIB[it.s.type].role === 'term') {
      const key = A.devKey.get(it.s.id) ?? '-?';
      (m.get(key) || m.set(key, []).get(key)!).push(it);
    }
  return [...m.entries()]
    .map(([key, list]) => ({
      key,
      name: keyName(A, key),
      rows: list
        .sort((a, b) => tagSort(String(a.s.props.pinNo ?? ''), String(b.s.props.pinNo ?? '')) || placedOrder(a, b))
        .map((it) => {
          const p = potentialOf(A, pinNode(it.s, 0));
          return {
            it,
            no: it.s.props.pinNo || '',
            inner: pinTargets(A, it, 0),
            outer: pinTargets(A, it, 1),
            pot: p ? p.name || p.ptype : '',
            bridge: it.s.props.bridge || '',
            part: it.s.part,
            loc: '/' + loc(it),
          };
        }),
    }))
    .sort((a, b) => tagSort(a.name, b.name));
}

/** Numbers the terminals 1…n (or from `start`) in the given row order. */
export function renumberStrip(rows: TermRow[], start = 1) {
  rows.forEach((r, i) => (r.it.s.props = { ...r.it.s.props, pinNo: String(start + i) }));
}

/** Row order following the schematic: page, then left to right, then top to bottom. */
export const schematicOrder = (rows: TermRow[]) => [...rows].sort((a, b) => placedOrder(a.it, b.it));

/** Duplicate terminal numbers within a strip. */
export function duplicateNumbers(rows: TermRow[]): Set<string> {
  const seen = new Set<string>(), dup = new Set<string>();
  for (const r of rows) {
    if (!r.no) continue;
    if (seen.has(r.no)) dup.add(r.no);
    seen.add(r.no);
  }
  return dup;
}

/** Accessories of a strip: an end plate, and a jumper between each pair of bridged terminals. */
export function stripAccessories(rows: TermRow[]): { endPlates: number; jumpers: number; groups: number } {
  const g = new Map<string, number>();
  for (const r of rows) if (r.bridge) g.set(r.bridge, (g.get(r.bridge) ?? 0) + 1);
  let jumpers = 0;
  for (const n of g.values()) jumpers += Math.max(0, n - 1);
  return { endPlates: rows.length ? 1 : 0, jumpers, groups: [...g.values()].filter((n) => n > 1).length };
}

/** Bridge groups that join terminals of different potentials (a short circuit), judged without the bridges. */
export function bridgeConflicts(pr: Project, rows: TermRow[]): Set<string> {
  const bridged = rows.filter((r) => r.bridge);
  if (bridged.length < 2) return new Set();
  const saved = bridged.map((r) => r.it.s.props);
  let B: Analysis;
  try {
    for (const r of bridged) {
      const p = { ...r.it.s.props };
      delete p.bridge;
      r.it.s.props = p;
    }
    B = analyze(pr);
  } finally {
    bridged.forEach((r, i) => (r.it.s.props = saved[i]));
  }
  const g = new Map<string, Set<string>>(), bad = new Set<string>();
  for (const r of bridged) {
    const p = potentialOf(B, pinNode(r.it.s, 0)), name = p ? p.name || p.ptype : '';
    if (!name) continue;
    const set = g.get(r.bridge) ?? g.set(r.bridge, new Set()).get(r.bridge)!;
    set.add(name);
    if (set.size > 1) bad.add(r.bridge);
  }
  return bad;
}

/* ---------------- connections ---------------- */

export interface NetRow {
  /** union-find root of the net */
  root: string;
  no: string;
  pot: string;
  /** device pins on the net */
  ends: string[];
  wires: { w: Wire; pi: number }[];
  /** common cross-section / colour; '*' when the wires differ */
  cs: string;
  col: string;
  pages: number[];
}

export function netRows(pr: Project, A: Analysis): NetRow[] {
  const m = new Map<string, NetRow>();
  pr.pages.forEach((p, pi) =>
    p.wires.forEach((w) => {
      const r = A.uf.find(wireNode(w.id));
      let n = m.get(r);
      if (!n) {
        const pot = A.pot.get(A.pu.find(wireNode(w.id)));
        m.set(r, (n = { root: r, no: A.netLabel.get(r) ?? '', pot: pot ? pot.name || pot.ptype : '', ends: [], wires: [], cs: w.cs, col: w.col, pages: [] }));
      }
      n.wires.push({ w, pi });
      if (n.cs !== w.cs) n.cs = '*';
      if (n.col !== w.col) n.col = '*';
      if (!n.pages.includes(pi)) n.pages.push(pi);
    }),
  );
  for (const it of A.all) {
    const role = LIB[it.s.type].role;
    if (['brk', 'text', 'pot', 'cable', 'sl', 'box'].includes(role)) continue;
    LIB[it.s.type].pins.forEach((_, i) => {
      const n = m.get(A.uf.find(pinNode(it.s, i)));
      if (n) n.ends.push(pinName(A, it.s, i));
    });
  }
  const num = (s: string) => {
    const v = parseInt(s, 10);
    return Number.isFinite(v) ? v : 1e9;
  };
  return [...m.values()].sort((a, b) => num(a.no) - num(b.no) || tagSort(a.no, b.no));
}

/* ---------------- devices ---------------- */

export interface DeviceRow {
  key: string;
  name: string;
  /** main function (or first symbol) of the device */
  main: Placed;
  all: Placed[];
  locs: string;
}

/** Devices with a tag: main functions, PLC cards and terminal strips excluded (they have their own navigators). */
export function deviceRows(A: Analysis): DeviceRow[] {
  const m = new Map<string, Placed[]>();
  for (const it of A.all) {
    const role = LIB[it.s.type].role;
    if (!it.s.tag || !['main', 'aux', 'sl'].includes(role)) continue;
    const key = A.devKey.get(it.s.id)!;
    (m.get(key) || m.set(key, []).get(key)!).push(it);
  }
  return [...m.entries()]
    .map(([key, all]) => {
      all.sort(placedOrder);
      return { key, name: keyName(A, key), main: all.find((x) => LIB[x.s.type].role === 'main') ?? all[0], all, locs: all.map((x) => '/' + loc(x)).join(' ') };
    })
    .sort((a, b) => tagSort(a.name, b.name));
}

/** Renames a device: every symbol of it (main, contacts) gets the new tag. */
export function renameDevice(row: DeviceRow, tag: string) {
  for (const it of row.all) it.s.tag = tag;
}
